import { authRoutes } from '@auth/routes';
import { ButtonType, Spinner, Button } from '@soteria/design-system-ve';
import { ErrorBanner } from '@soteria-ui/src/core/components';
import type { DomainError } from '@soteria-ui/src/core/domain';
import { useNavigate } from 'react-router-dom';

import * as styles from './AuthCard.css';

export interface AuthCardProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  error?: DomainError[] | string | null;
  errorBannerTitle?: string;
  onRetry?: () => void;
}

export function AuthCard({ title, description, children, isLoading, loadingText, error, errorBannerTitle, onRetry }: AuthCardProps) {
  const navigate = useNavigate();

  const headerContent = (title?: string, description?: string) => (
    <>
      {/* Logo */}
      <div className={styles.logoContainer}>
        <img alt="Microsoft logo" className={styles.logo} src="/microsoft-logo.svg" />
      </div>

      {/* Title */}
      {title && <h1 className={styles.title}>{title}</h1>}

      {/* Description */}
      {description && <p className={styles.subtitle}>{description}</p>}
    </>
  );

  const loadingContent = () => (
    <div className={styles.loadingContainer}>
      <Spinner color="primary" size="medium" />
      <p className={styles.loadingText}>{loadingText || 'Loading...'}</p>
    </div>
  );

  const errorContent = () => (
    <div className={styles.errorStateContainer}>
      {/* Error content at top */}
      <div className={styles.errorContent}>
        {headerContent('Oops, something went wrong', 'Please try again.')}

        {/* Error Message */}
        <ErrorBanner textOnly errors={error || undefined} title={errorBannerTitle || undefined} />
      </div>

      {/* TRY AGAIN button at bottom */}
      <Button buttonType={ButtonType.Primary} className={styles.tryAgainButton} onClick={onRetry || (() => navigate(authRoutes.login))}>
        TRY AGAIN
      </Button>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return loadingContent();
    }

    if (error) {
      return errorContent();
    }

    return (
      <div className={styles.innerContainer}>
        {headerContent(title, description)}

        {/* Show children */}
        {children}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginCard}>{renderContent()}</div>
    </div>
  );
}
